import { useState, useEffect, useCallback, useRef } from 'react'
import {
	Box,
	InputBase,
	CircularProgress,
	Alert,
	Snackbar,
	Dialog,
	useMediaQuery,
	useTheme
} from '@mui/material'
import AddAppointmentDialog from './AddAppointmentDialog'
import { supabase } from '../utils/supabase'
import posthog, { isPostHogConfigured } from '../posthog'
import { getFullName } from '../utils/clientUtils'
import { formatPhoneToE164, isValidPhoneNumber } from '../utils/phoneUtils'
import { canEmployeePerformService } from '../utils/appointmentUtils'
import { getCurrencySymbol } from '../utils/currencyUtils'
import { L } from './ui/LavDropdown'
import MsgIcon, { AiBadge } from './messaging/MsgIcon'
import { MessageBubble, DaySeparator } from './messaging/MessageBubble'
import { ProfileSections, DesktopProfileSidebar, MsgAvatar, ViewProfileLink } from './messaging/ClientProfile'
import OverlayScrollThumb from './ui/OverlayScrollThumb'
import {
	formatListTime,
	dayLabel,
	smsCounter
} from './messaging/messagingUtils'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

// Conversation ordering: most recent message first, clients with no messages
// last (alphabetically). Shared by the initial load and the local update we do
// after sending, so the sidebar can be kept fresh without a refetch.
const byLastMessage = (a, b) => {
	if (a.lastMessage && b.lastMessage) {
		return new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
	}
	if (a.lastMessage) return -1
	if (b.lastMessage) return 1
	return a.name.localeCompare(b.name)
}

function MessagesManager({
	merchant,
	preselectedClient = null,
	onClientSelected,
	unreadByClient = {},
	markRead = () => {},
	// Jump to this client's profile on the Clients tab ("View profile" in the
	// profile sidebar / mobile details screen). Optional — without it the
	// identity blocks stay non-interactive.
	onOpenClientProfile = null
}) {
	const [clients, setClients] = useState([])
	const [filteredClients, setFilteredClients] = useState([])
	const [selectedClient, setSelectedClient] = useState(null)
	const [messages, setMessages] = useState([])
	const [messageText, setMessageText] = useState('')
	const [searchQuery, setSearchQuery] = useState('')
	const [loading, setLoading] = useState(false)
	const [sending, setSending] = useState(false)
	const [error, setError] = useState('')
	const [loadingMessages, setLoadingMessages] = useState(false)
	// Web Notification permission state — re-checked on mount and after the user
	// clicks the CTA. We only show the CTA when permission is in its initial
	// 'default' state; 'granted' = silent, 'denied' = the user must unblock it
	// in browser settings (we can't re-prompt).
	const [notifPermission, setNotifPermission] = useState(
		typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
	)
	const messagesEndRef = useRef(null)

	const handleEnableNotifications = useCallback(async () => {
		if (typeof Notification === 'undefined') return
		try {
			const result = await Notification.requestPermission()
			setNotifPermission(result)
		} catch (e) {
			console.warn('Notification.requestPermission failed', e)
		}
	}, [])

	// AI appointment prefill state.
	//   aiBusy → spinner on the persistent AI button while Claude is parsing.
	//   prefilledAppointmentData → bundle passed into AddAppointmentDialog when we open it.
	//   aiContext → header subtitle + note shown inside the AI dialog.
	//   aiSnack → bottom snackbar for errors / booking confirmation.
	const [aiBusy, setAiBusy] = useState(false)
	const [addAppointmentOpen, setAddAppointmentOpen] = useState(false)
	const [prefilledAppointmentData, setPrefilledAppointmentData] = useState(null)
	const [aiContext, setAiContext] = useState(null)
	const [aiSnack, setAiSnack] = useState(null) // { severity, message } | null

	// Client profile data (pets + upcoming appointments) for the selected
	// conversation — powers the desktop sidebar, the mobile details screen,
	// and the quick-reply template placeholders.
	const [profilePets, setProfilePets] = useState([])
	const [profileUpcoming, setProfileUpcoming] = useState([])
	const [profileLoading, setProfileLoading] = useState(false)

	// Mobile navigation within the messages tab: inbox (no selected client) →
	// thread → client details.
	const [mobileView, setMobileView] = useState('thread') // 'thread' | 'profile'

	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	// The profile sidebar needs ~290px on top of list + thread; hide it on
	// narrower desktop windows instead of squeezing the thread.
	const showProfileSidebar = useMediaQuery(theme.breakpoints.up('lg'))

	const merchantTz = merchant?.timezone || dayjs.tz.guess()
	const currencySymbol = getCurrencySymbol(merchant?.currency)
	const formatCurrency = useCallback(
		(cents) => `${currencySymbol}${((cents || 0) / 100).toFixed(2)}`,
		[currencySymbol]
	)

	// Load clients with their last message.
	// `silent` skips the loading spinner — used for background refreshes (realtime
	// echoes) so the sidebar doesn't blank out every time a message arrives.
	const loadClients = useCallback(async ({ silent = false } = {}) => {
		if (!merchant?.id) return

		if (!silent) setLoading(true)
		try {
			const { data: clientsData, error: clientsError } = await supabase
				.from('clients')
				.select('*')
				.eq('merchant_id', merchant.id)
				.order('name')

			if (clientsError) throw clientsError

			// Load last message for each client
			const { data: messagesData, error: messagesError } = await supabase
				.from('sms_messages')
				.select('*')
				.eq('merchant_id', merchant.id)
				.order('created_at', { ascending: false })

			if (messagesError) throw messagesError

			// Group messages by client and get the last one
			const lastMessages = {}
			messagesData?.forEach(msg => {
				if (!lastMessages[msg.client_id]) {
					lastMessages[msg.client_id] = msg
				}
			})

			// Combine clients with their last messages
			const clientsWithMessages = (clientsData || []).map(client => ({
				...client,
				lastMessage: lastMessages[client.id]
			}))

			// Sort by last message time (most recent first), then by name
			clientsWithMessages.sort(byLastMessage)

			setClients(clientsWithMessages)
			setFilteredClients(clientsWithMessages)
		} catch (e) {
			console.error('Error loading clients:', e)
			setError('Failed to load clients')
		} finally {
			if (!silent) setLoading(false)
		}
	}, [merchant?.id])

	useEffect(() => {
		if (merchant?.id) {
			loadClients()
		}
	}, [merchant, loadClients])

	// Load messages for selected client
	const loadMessages = useCallback(async (clientId) => {
		if (!merchant?.id || !clientId) return

		setLoadingMessages(true)
		try {
			const { data, error } = await supabase
				.from('sms_messages')
				.select('*')
				.eq('merchant_id', merchant.id)
				.eq('client_id', clientId)
				.order('created_at', { ascending: true })

			if (error) throw error

			setMessages(data || [])
		} catch (e) {
			console.error('Error loading messages:', e)
			setError('Failed to load messages')
		} finally {
			setLoadingMessages(false)
		}
	}, [merchant?.id])

	// Handle client selection
	const handleSelectClient = (client) => {
		setSelectedClient(client)
		setError('')
		setMessageText('')
		setMobileView('thread')
		loadMessages(client.id)
		// Clear unread state for this client — they're literally looking at it.
		markRead(client.id)
		// Notify parent that client was selected (to clear preselection)
		if (onClientSelected) {
			onClientSelected()
		}
	}

	// Handle preselected client
	useEffect(() => {
		if (preselectedClient && clients.length > 0) {
			// Find the client in the loaded clients list
			const client = clients.find(c => c.id === preselectedClient.id)
			if (client) {
				handleSelectClient(client)
			} else {
				// If client not found in list, add it temporarily and select it
				const clientWithMessage = { ...preselectedClient, lastMessage: null }
				setClients(prev => [clientWithMessage, ...prev])
				setFilteredClients(prev => [clientWithMessage, ...prev])
				setSelectedClient(preselectedClient)
				setMobileView('thread')
				loadMessages(preselectedClient.id)
				if (onClientSelected) {
					onClientSelected()
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preselectedClient, clients.length])

	// Handle search
	useEffect(() => {
		if (searchQuery.trim() === '') {
			setFilteredClients(clients)
		} else {
			const query = searchQuery.toLowerCase()
			const filtered = clients.filter(client =>
				getFullName(client).toLowerCase().includes(query) ||
				client.phone_number?.toLowerCase().includes(query) ||
				client.email?.toLowerCase().includes(query)
			)
			setFilteredClients(filtered)
		}
	}, [searchQuery, clients])

	// Pets + upcoming appointments for the selected client (profile panel and
	// quick-reply placeholders).
	useEffect(() => {
		if (!selectedClient?.id || !merchant?.id) {
			setProfilePets([])
			setProfileUpcoming([])
			return
		}
		let cancelled = false
		setProfileLoading(true)
		Promise.all([
			supabase
				.from('pets')
				.select('id, name, species, breed')
				.eq('client_id', selectedClient.id)
				.order('name'),
			supabase
				.from('appointments')
				.select(`
					id,
					start_ts,
					end_ts,
					add_ons,
					service_price,
					service_types(name, category, appointment_color, price),
					employees(first_name, last_name),
					pets(name)
				`)
				.eq('merchant_id', merchant.id)
				.eq('user_id', selectedClient.id)
				.not('status', 'eq', 'cancelled')
				.gte('start_ts', new Date().toISOString())
				.order('start_ts', { ascending: true })
				.limit(5),
		])
			.then(([petsRes, apptsRes]) => {
				if (cancelled) return
				if (petsRes.error) console.error('Error loading pets:', petsRes.error)
				else setProfilePets(petsRes.data || [])
				if (apptsRes.error) console.error('Error loading upcoming appointments:', apptsRes.error)
				else setProfileUpcoming(apptsRes.data || [])
			})
			.finally(() => {
				if (!cancelled) setProfileLoading(false)
			})
		return () => { cancelled = true }
	}, [selectedClient?.id, merchant?.id])

	// Send message
	const handleSendMessage = async () => {
		// Ignore Enter / clicks while a send is already in flight.
		if (sending) return
		if (!messageText.trim() || !selectedClient || !merchant?.id) return

		if (!merchant.twilio_phone_number) {
			setError('Messaging is not enabled. Purchase a number in Settings → Messaging.')
			return
		}

		if (!selectedClient.phone_number) {
			setError('This client does not have a phone number')
			return
		}

		if (!isValidPhoneNumber(selectedClient.phone_number)) {
			setError('Invalid phone number format. Please update the client\'s phone number.')
			return
		}

		const formattedPhone = formatPhoneToE164(selectedClient.phone_number)
		if (!formattedPhone) {
			setError('Unable to format phone number. Please check the client\'s phone number.')
			return
		}

		setSending(true)
		setError('')

		try {
			// send-sms now persists the message row server-side (atomic with the
			// Twilio API call). We just invoke it and rely on realtime + refetch
			// to update the UI.
			const { data, error: invokeError } = await supabase.functions.invoke('send-sms', {
				body: {
					merchantId: merchant.id,
					clientId: selectedClient.id,
					to: formattedPhone,
					message: messageText.trim(),
				},
			})

			if (invokeError) {
				// On a non-2xx, supabase-js only says "non-2xx status code"; the
				// function's own { error, details } body lives on context.
				let detail = ''
				try {
					const payload = invokeError.context ? await invokeError.context.json() : null
					detail = payload?.error || payload?.details || ''
				} catch { /* body wasn't JSON — fall through to the generic message */ }
				throw new Error(detail || invokeError.message || 'Failed to send SMS')
			}
			if (data?.error) throw new Error(data.details || data.error)
			if (data?.warning) {
				// SMS was sent but the row didn't get inserted. Don't pretend it failed;
				// surface the warning so the merchant knows the DB record may be missing.
				console.warn('send-sms warning:', data.warning)
			}

			setMessageText('')

			// Append the row send-sms just inserted rather than refetching. A refetch
			// flips loadingMessages, which unmounts the bubbles — and once they
			// remount the scroll-to-bottom effect animates all the way from the top.
			// Appending keeps the thread in place and just nudges to the new bubble.
			// The realtime INSERT echo dedupes on id, so this is safe either way.
			const sentRow = data?.row
			if (sentRow) {
				setMessages(prev => (
					prev.some(m => m.id === sentRow.id) ? prev : [...prev, sentRow]
				))
				// Refresh the sidebar preview + ordering locally; loadClients() would
				// put a spinner over the whole conversation list.
				setClients(prev => prev
					.map(c => (c.id === sentRow.client_id ? { ...c, lastMessage: sentRow } : c))
					.sort(byLastMessage)
				)
			}
		} catch (e) {
			console.error('Error sending message:', e)
			setError(e.message || 'Failed to send message')
		} finally {
			setSending(false)
		}
	}

	// Handle enter key press
	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			handleSendMessage()
		}
	}

	// Persistent "Create appointment (AI)" button. When the thread has
	// messages, ask Claude (via the parse-appointment-from-sms edge function)
	// to extract appointment details and open the AddAppointmentDialog
	// pre-filled with whatever it confidently resolved. With no messages we
	// skip the AI call and open the form with just the client seeded.
	//
	// We send messages newest-first and instruct the model to consider ONLY the
	// most recent appointment request — older bookings discussed earlier in the
	// thread are ignored.
	const handleAiPrefill = useCallback(async () => {
		if (!selectedClient?.id || !merchant?.id) return
		const first = getFullName(selectedClient).trim().split(/\s+/)[0]

		if (!messages || messages.length === 0) {
			setPrefilledAppointmentData({ client: selectedClient })
			setAiContext({
				subtitle: `No messages with ${first} yet — fill in the details`,
				note: 'There’s no conversation to scan yet — pick the pet, service, date, and time to book.',
			})
			setAddAppointmentOpen(true)
			return
		}

		setAiBusy(true)
		try {
			// Fetch the client's pets, services (bookable), add-ons, employees, and
			// their most recent appointment in parallel — the first four are the same
			// shapes that AddSingleAppointment loads on open; the last one seeds any
			// fields the AI can't resolve.
			const [petsRes, servicesRes, addOnsRes, employeesRes, latestApptRes] = await Promise.all([
				supabase
					.from('pets')
					.select('id, name, species, breed, weight')
					.eq('client_id', selectedClient.id)
					.order('name'),
				supabase
					.from('service_types')
					.select('id, name, duration_minutes, category, appointment_color, price')
					.eq('merchant_id', merchant.id)
					.eq('is_service', true)
					.order('name'),
				supabase
					.from('service_types')
					.select('id, name, price, duration_minutes')
					.eq('merchant_id', merchant.id)
					.eq('is_add_on', true)
					.order('name'),
				supabase
					.from('employees')
					.select('id, first_name, last_name, services')
					.eq('merchant_id', merchant.id)
					.eq('active', true)
					.order('first_name'),
				supabase
					.from('appointments')
					.select('pet_id, service_id, employee_id, add_ons')
					.eq('merchant_id', merchant.id)
					.eq('user_id', selectedClient.id)
					.not('status', 'eq', 'cancelled')
					.order('start_ts', { ascending: false })
					.limit(1),
			])

			const firstError =
				petsRes.error || servicesRes.error || addOnsRes.error || employeesRes.error
			if (firstError) throw firstError
			// The history fallback is best-effort — a failed fetch just means no seeding.
			if (latestApptRes.error) {
				console.warn('Could not load latest appointment for prefill:', latestApptRes.error)
			}

			const pets = petsRes.data || []
			const services = servicesRes.data || []
			const addOns = addOnsRes.data || []
			const employees = employeesRes.data || []

			// Messages are stored oldest-first in `messages` state. Send the
			// most recent ones first so the model anchors on the latest request.
			const newestFirst = [...messages]
				.slice(-20)
				.reverse()
				.map(m => ({
					direction: m.direction || 'outbound',
					message_text: m.message_text || '',
					created_at: m.created_at,
				}))

			const currentTime = dayjs().tz(merchantTz).format()
			const posthogDistinctId = isPostHogConfigured ? posthog.get_distinct_id() : undefined

			const { data, error: fnError } = await supabase.functions.invoke(
				'parse-appointment-from-sms',
				{
					body: {
						merchantId: merchant.id,
						clientId: selectedClient.id,
						posthogDistinctId,
						messages: newestFirst,
						client: {
							id: selectedClient.id,
							name: selectedClient.name,
							last_name: selectedClient.last_name,
							phone_number: selectedClient.phone_number,
						},
						pets,
						services,
						addOns,
						employees,
						merchantTimezone: merchantTz,
						currentTime,
					},
				}
			)
			if (fnError) throw fnError
			if (data?.error) throw new Error(data.error)

			const r = data?.result
			if (!r) throw new Error('No result from AI')

			// Resolve ids → objects, falling back to null when the model returned
			// something we don't have. The dialog will treat null fields as
			// "still needs input from the merchant".
			const resolved = {
				client: selectedClient,
				date: r.date || null,
				time: r.time || null,
				pet: r.petId ? pets.find(p => p.id === r.petId) || null : null,
				service: r.serviceId ? services.find(s => s.id === r.serviceId) || null : null,
				addOns: Array.isArray(r.addOnIds)
					? addOns.filter(a => r.addOnIds.includes(a.id))
					: [],
				employee: r.employeeId ? employees.find(e => e.id === r.employeeId) || null : null,
			}

			// True when `employee` already has an appointment overlapping the
			// AI-resolved date/time — same status + timestamp convention as the
			// overview's overlap warning. Without a date, time, and service
			// (for duration) there's nothing to check.
			const employeeHasOverlap = async (employee, { date, time, service }) => {
				if (!date || !time || !service) return false
				const start = dayjs.tz(`${date} ${time}`, merchantTz)
				if (!start.isValid()) return false
				const end = start.add(service.duration_minutes || 60, 'minute')
				const { data: overlaps, error: overlapError } = await supabase
					.from('appointments')
					.select('id')
					.eq('merchant_id', merchant.id)
					.eq('employee_id', employee.id)
					.in('status', ['booked', 'bookPending'])
					.lt('start_ts', end.utc().toISOString())
					.gt('end_ts', start.utc().toISOString())
					.limit(1)
				// Can't verify → treat as busy so we don't prefill a conflict.
				if (overlapError) return true
				return (overlaps || []).length > 0
			}

			const aiFoundAny = !!(
				resolved.date || resolved.time || resolved.pet ||
				resolved.service || resolved.employee || resolved.addOns.length
			)

			// Fields the AI couldn't resolve get seeded from the client's most
			// recent appointment — regulars usually rebook the same pet, service,
			// and groomer. The employee fallback is skipped when they can't perform
			// the resolved service or already have an appointment overlapping the
			// AI-resolved date/time.
			const historyFilled = []
			const latest = latestApptRes.data?.[0]
			if (latest) {
				if (!resolved.pet && latest.pet_id) {
					resolved.pet = pets.find(p => p.id === latest.pet_id) || null
					if (resolved.pet) historyFilled.push('pet')
				}
				if (!resolved.service && latest.service_id) {
					resolved.service = services.find(s => s.id === latest.service_id) || null
					if (resolved.service) {
						historyFilled.push('service')
						// Add-ons only ride along with the service they were booked with.
						if (resolved.addOns.length === 0 && Array.isArray(latest.add_ons)) {
							resolved.addOns = addOns.filter(a =>
								latest.add_ons.some(x => x.service_id === a.id)
							)
						}
					}
				}
				if (!resolved.employee && latest.employee_id) {
					const candidate = employees.find(e => e.id === latest.employee_id) || null
					const capable = candidate &&
						(!resolved.service || canEmployeePerformService(candidate, resolved.service.id))
					if (capable && !(await employeeHasOverlap(candidate, resolved))) {
						resolved.employee = candidate
						historyFilled.push('employee')
					}
				}
			}

			// Surface the model's reasoning + any missing fields inside the
			// dialog so the merchant knows what still needs review before booking.
			const foundAny = aiFoundAny || historyFilled.length > 0
			const stillMissing = (Array.isArray(r.missing) ? r.missing : []).filter(
				m => !historyFilled.includes(String(m).toLowerCase())
			)
			const missingPretty = stillMissing.length > 0
				? `Needs review: ${stillMissing.join(', ')}.`
				: ''
			const historyPretty = historyFilled.length > 0
				? `Pre-filled ${historyFilled.join(', ')} from ${first}’s last appointment.`
				: ''
			setAiContext({
				subtitle: aiFoundAny
					? `Auto-filled from ${first}’s messages · review & book`
					: foundAny
						? `Pre-filled from ${first}’s last appointment · review & book`
						: `AI scanned ${first}’s messages — fill in the rest`,
				note: foundAny
					? [r.notes, historyPretty, missingPretty].filter(Boolean).join(' ') ||
						'AI-filled fields were pulled from the conversation — adjust anything before booking.'
					: 'AI couldn’t find booking details in this conversation — pick the service, date, and time to book.',
			})
			setPrefilledAppointmentData(resolved)
			setAddAppointmentOpen(true)
		} catch (e) {
			console.error('AI prefill failed:', e)
			setAiSnack({
				severity: 'error',
				message: e.message || 'Could not parse appointment from messages.',
			})
		} finally {
			setAiBusy(false)
		}
	}, [selectedClient, merchant, messages, merchantTz])

	const closeAddAppointment = useCallback(() => {
		setAddAppointmentOpen(false)
		setPrefilledAppointmentData(null)
		setAiContext(null)
	}, [])

	const handleAppointmentCreated = useCallback(() => {
		setAddAppointmentOpen(false)
		setPrefilledAppointmentData(null)
		setAiContext(null)
		setAiSnack({ severity: 'success', message: 'Appointment created.' })
	}, [])

	// When the open conversation switches, flag that the next messages update is
	// a fresh load — so we jump straight to the bottom instead of animating a
	// scroll from the top of the newly-loaded thread.
	const pendingJumpRef = useRef(true)
	useEffect(() => {
		pendingJumpRef.current = true
	}, [selectedClient?.id])

	// Scroll to bottom when messages change. Jump instantly on a fresh load
	// (client switch / initial load); smooth-scroll only when a message is
	// appended to the thread that's already open.
	useEffect(() => {
		const behavior = pendingJumpRef.current ? 'auto' : 'smooth'
		pendingJumpRef.current = false
		messagesEndRef.current?.scrollIntoView({ behavior })
	}, [messages])

	// Always-on realtime subscription for this merchant's inbox.
	// Fires for every new sms_messages INSERT (both inbound and outbound):
	//   - If the row's client is the currently open conversation → append it.
	//   - Either way → refresh the sidebar so last-message preview + sort order
	//     update, and any auto-created clients from twilio-inbound appear.
	//
	// We use a ref for selectedClient.id so the callback always sees the latest
	// value without us having to tear down + resubscribe whenever the user
	// switches conversations.
	const selectedClientIdRef = useRef(null)
	useEffect(() => {
		selectedClientIdRef.current = selectedClient?.id || null
	}, [selectedClient?.id])

	// Stable ref to markRead so the realtime channel doesn't need to be
	// re-subscribed every time the parent re-renders.
	const markReadRef = useRef(markRead)
	useEffect(() => {
		markReadRef.current = markRead
	}, [markRead])

	useEffect(() => {
		if (!merchant?.id) return

		const channel = supabase
			.channel(`sms_messages:merchant:${merchant.id}`)
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'sms_messages',
					filter: `merchant_id=eq.${merchant.id}`,
				},
				(payload) => {
					const newMsg = payload.new
					if (selectedClientIdRef.current === newMsg.client_id) {
						setMessages(prev => {
							// send-sms returns synchronously and we reload via
							// loadMessages() — guard against the realtime echo
							// re-appending the same row.
							if (prev.some(m => m.id === newMsg.id)) return prev
							return [...prev, newMsg]
						})
						// If an inbound arrives in the currently-open conversation
						// while the tab is visible, the merchant is already looking
						// at it — mark read immediately so the dot doesn't blink on.
						if (
							newMsg.direction === 'inbound' &&
							typeof document !== 'undefined' &&
							document.visibilityState === 'visible'
						) {
							markReadRef.current(newMsg.client_id)
						}
					}
					// Background refresh — no spinner, so an arriving message never
					// blanks the conversation list.
					loadClients({ silent: true })
				}
			)
			.subscribe()

		return () => {
			supabase.removeChannel(channel)
		}
	}, [merchant?.id, loadClients])

	// Gate the entire UI on the merchant having a provisioned Twilio number.
	// Without one, sending/receiving SMS is impossible, so we surface a clear
	// pointer to Settings → Messaging instead of an empty inbox.
	if (merchant && !merchant.twilio_phone_number) {
		return (
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				height: '100%',
				p: 4,
				textAlign: 'center',
				bgcolor: L.surface,
				fontFamily: L.font,
			}}>
				<Box sx={{ color: L.muted, mb: 2 }}>
					<MsgIcon name="msg" size={52} sw={1.2} />
				</Box>
				<Box sx={{ fontSize: '17px', fontWeight: 750, color: L.text, mb: 1 }}>
					Messaging is not enabled
				</Box>
				<Box sx={{ fontSize: '13.5px', color: L.muted, fontWeight: 550, maxWidth: 420, mb: 0.5 }}>
					Purchase a dedicated phone number to send and receive text messages with your clients.
				</Box>
				<Box sx={{ fontSize: '13.5px', color: L.muted, fontWeight: 550, maxWidth: 420 }}>
					Go to <b>Settings → Messaging</b> to get started.
				</Box>
			</Box>
		)
	}

	const clientName = selectedClient ? getFullName(selectedClient) : ''
	const clientFirst = clientName.trim().split(/\s+/)[0] || ''

	// ---- shared pieces ----

	const smsBadge = (
		<Box sx={{
			display: 'inline-flex', alignItems: 'center', gap: '5px', bgcolor: L.primarySoft,
			color: L.primaryDeep, fontSize: '11px', fontWeight: 750, letterSpacing: '0.04em',
			p: '4px 9px', borderRadius: '99px', flex: 'none',
		}}>
			<MsgIcon name="msg" size={12} />
			<span>SMS</span>
		</Box>
	)

	const notifCta = notifPermission === 'default' && (
		<Box
			onClick={handleEnableNotifications}
			sx={{
				display: 'flex', alignItems: 'center', gap: '8px', m: '0 18px 12px',
				p: '8px 11px', borderRadius: L.rSm, cursor: 'pointer',
				bgcolor: L.primarySoft, color: L.primaryDeep, fontSize: '12.5px', fontWeight: 650,
				'&:hover': { bgcolor: '#E2DAF7' },
			}}
		>
			<MsgIcon name="bell" size={15} />
			<span>Enable desktop notifications</span>
		</Box>
	)

	const renderMessages = () => {
		if (loadingMessages) {
			return (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
					<CircularProgress size={24} sx={{ color: L.primary }} />
				</Box>
			)
		}
		if (messages.length === 0) {
			return (
				<Box sx={{ textAlign: 'center', p: 3 }}>
					<Box component="span" sx={{ fontSize: '13px', color: L.muted, fontWeight: 600 }}>
						No messages yet. Send your first message below!
					</Box>
				</Box>
			)
		}
		return (
			<>
				{messages.map((msg, index) => {
					const prevMsg = index > 0 ? messages[index - 1] : null
					const showDay = !prevMsg || !dayjs(msg.created_at).isSame(dayjs(prevMsg.created_at), 'day')
					return (
						<Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
							{showDay && <DaySeparator label={dayLabel(msg.created_at)} />}
							<MessageBubble msg={msg} maxWidth={isMobile ? '80%' : '62%'} />
						</Box>
					)
				})}
				<div ref={messagesEndRef} />
			</>
		)
	}

	const aiButtonDesktop = (
		<Box
			component="button"
			type="button"
			onClick={handleAiPrefill}
			disabled={aiBusy}
			sx={{
				display: 'flex', alignItems: 'center', gap: '7px', flex: 'none', height: 38,
				px: '14px', border: '1.5px solid #D7CCF5', borderRadius: '10px', bgcolor: L.field,
				color: L.primaryDeep, fontFamily: 'inherit', fontSize: '13px', fontWeight: 700,
				whiteSpace: 'nowrap', cursor: 'pointer',
				'&:hover': { bgcolor: L.primarySoft, borderColor: L.primary },
				'&:disabled': { opacity: 0.6, cursor: 'default' },
			}}
		>
			{aiBusy
				? <CircularProgress size={14} sx={{ color: L.primaryDeep }} />
				: <MsgIcon name="auto" size={14} />}
			<span>Create appointment</span>
			<AiBadge />
		</Box>
	)

	const composerInput = (variant) => (
		<Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
			<InputBase
				fullWidth
				multiline
				maxRows={4}
				placeholder={clientFirst ? `Text ${clientFirst}…` : 'Type a message…'}
				value={messageText}
				onChange={(e) => setMessageText(e.target.value)}
				onKeyDown={handleKeyDown}
				disabled={sending || !selectedClient?.phone_number}
				sx={{
					border: `1.5px solid ${L.border}`, borderRadius: variant === 'mobile' ? '20px' : L.rSm,
					bgcolor: L.field, fontFamily: L.font, color: L.text,
					fontSize: variant === 'mobile' ? '15px' : '13.5px', lineHeight: 1.4,
					p: variant === 'mobile' ? '9px 14px' : '10px 70px 10px 13px',
					'&.Mui-focused': { borderColor: L.primary },
					'& textarea::placeholder': { color: L.muted, opacity: 1 },
				}}
			/>
			{variant !== 'mobile' && (
				<Box sx={{
					position: 'absolute', right: 11, bottom: 12, fontSize: '10.5px', fontWeight: 700,
					color: L.muted, letterSpacing: '0.03em', pointerEvents: 'none',
				}}>
					{smsCounter(messageText)}
				</Box>
			)}
		</Box>
	)

	const sendButton = (variant) => (
		<Box
			component="button"
			type="button"
			aria-label="Send"
			onClick={handleSendMessage}
			disabled={!messageText.trim() || sending || !selectedClient?.phone_number}
			sx={{
				width: variant === 'mobile' ? 40 : 42, height: variant === 'mobile' ? 40 : 42,
				flex: 'none', borderRadius: variant === 'mobile' ? '50%' : L.rSm, border: 'none',
				bgcolor: L.primary, color: L.onPrimary, display: 'flex', alignItems: 'center',
				justifyContent: 'center', cursor: 'pointer', boxShadow: `0 2px 8px ${L.primarySoft}`,
				'&:hover': { bgcolor: L.primaryDeep },
				'&:disabled': { opacity: 0.45, cursor: 'default', boxShadow: 'none', bgcolor: L.primary },
			}}
		>
			{sending
				? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} />
				: <MsgIcon name="send" size={16} />}
		</Box>
	)

	// ---- desktop conversation list ----

	const renderConversationList = () => (
		<Box sx={{
			width: isMobile ? '100%' : 320, flex: 'none',
			borderRight: isMobile ? 'none' : `1px solid ${L.border}`,
			display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: L.bg,
		}}>
			{isMobile ? (
				<Box sx={{ p: '14px 18px 8px' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<Box component="h1" sx={{ fontSize: '30px', fontWeight: 800, m: 0, letterSpacing: '-0.02em', color: L.text }}>
							Messages
						</Box>
						{smsBadge}
					</Box>
					<Box sx={{
						display: 'flex', alignItems: 'center', gap: '8px', mt: '12px', height: 40,
						px: '12px', borderRadius: '11px', bgcolor: L.surface, color: L.muted,
					}}>
						<MsgIcon name="search" size={16} />
						<Box
							component="input"
							type="text"
							placeholder="Search"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							sx={{
								border: 'none', outline: 'none', bgcolor: 'transparent', fontFamily: 'inherit',
								fontSize: '15px', color: L.text, width: '100%',
								'&::placeholder': { color: L.muted },
							}}
						/>
					</Box>
				</Box>
			) : (
				<>
					<Box sx={{ p: '18px 18px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<Box component="h2" sx={{ fontSize: '19px', fontWeight: 700, m: 0, letterSpacing: '-0.01em', color: L.text }}>
							Messages
						</Box>
						{smsBadge}
					</Box>
					<Box sx={{
						display: 'flex', alignItems: 'center', gap: '8px', m: '0 18px 12px', height: 38,
						px: '11px', border: `1.5px solid ${L.border}`, borderRadius: L.rSm,
						bgcolor: L.field, color: L.muted, flex: 'none',
						'&:focus-within': { borderColor: L.primary },
					}}>
						<MsgIcon name="search" size={15} />
						<Box
							component="input"
							type="text"
							placeholder="Search clients…"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							sx={{
								border: 'none', outline: 'none', bgcolor: 'transparent', fontFamily: 'inherit',
								fontSize: '13.5px', color: L.text, width: '100%',
								'&::placeholder': { color: L.muted },
							}}
						/>
					</Box>
				</>
			)}

			{notifCta}

			<Box className="lav-overlay-scroll" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '0 10px 12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
				<OverlayScrollThumb />
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
						<CircularProgress size={24} sx={{ color: L.primary }} />
					</Box>
				) : filteredClients.length === 0 ? (
					<Box sx={{ p: 3, textAlign: 'center' }}>
						<Box component="span" sx={{ fontSize: '13px', color: L.muted, fontWeight: 600 }}>
							{searchQuery ? 'No clients found' : 'No clients yet'}
						</Box>
					</Box>
				) : (
					filteredClients.map((client) => {
						const isUnread = Boolean(unreadByClient[client.id])
						const isActive = !isMobile && selectedClient?.id === client.id
						const fullName = getFullName(client)
						const preview = client.lastMessage
							? `${client.lastMessage.direction === 'outbound' ? 'You: ' : ''}${client.lastMessage.message_text}`
							: client.phone_number || 'No messages yet'
						return (
							<Box
								component="button"
								type="button"
								key={client.id}
								onClick={() => handleSelectClient(client)}
								sx={{
									display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '10px',
									width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
									bgcolor: isActive ? L.primarySoft : 'transparent',
									borderRadius: isMobile ? '14px' : '12px',
									p: isMobile ? '11px 8px' : '10px', fontFamily: 'inherit', flex: 'none',
									'&:hover': { bgcolor: isActive ? L.primarySoft : L.surface },
								}}
							>
								<MsgAvatar
									name={fullName}
									size={isMobile ? 46 : 36}
									fontSize={isMobile ? 16 : 13}
									white={isActive}
								/>
								<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
									<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
										<Box component="strong" sx={{
											fontSize: isMobile ? '16px' : '14px', fontWeight: isMobile ? 700 : 750,
											color: L.text, flex: 1, minWidth: 0,
											overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
										}}>
											{fullName}
										</Box>
										{client.lastMessage && (
											<Box component="em" sx={{
												fontStyle: 'normal', fontSize: isMobile ? '12px' : '11px',
												color: isUnread ? L.primaryDeep : L.muted,
												// design: .mb-convo-top em is 600, .msg-item-top em is 650
												fontWeight: isMobile ? 600 : 650, flex: 'none', whiteSpace: 'nowrap',
											}}>
												{formatListTime(client.lastMessage.created_at)}
											</Box>
										)}
									</Box>
									<Box component="span" sx={{
										fontSize: isMobile ? '13.5px' : '12.5px',
										color: isUnread ? L.text : L.muted,
										// design: .mb-convo-prev is 500, .msg-item-preview is 550;
										// the unread bolding is ours, not the design's.
										fontWeight: isUnread ? 650 : (isMobile ? 500 : 550),
										whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
									}}>
										{preview}
									</Box>
								</Box>
								{isUnread && (
									<Box
										aria-label="unread"
										sx={{ width: 10, height: 10, flex: 'none', borderRadius: '50%', bgcolor: L.primary, ml: '6px' }}
									/>
								)}
							</Box>
						)
					})
				)}
			</Box>
		</Box>
	)

	// ---- thread (desktop + mobile) ----

	const renderErrorAlert = () => error && (
		<Alert severity="error" onClose={() => setError('')} sx={{ m: '8px 16px 0', fontFamily: L.font }}>
			{error}
		</Alert>
	)

	const renderDesktopThread = () => (
		<Box sx={{
			flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0,
			bgcolor: L.surface, position: 'relative',
		}}>
			{!selectedClient ? (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
					<Box component="span" sx={{ fontSize: '13.5px', color: L.muted, fontWeight: 600 }}>
						Select a client to view messages
					</Box>
				</Box>
			) : (
				<>
					{/* Thread header */}
					<Box sx={{
						display: 'flex', alignItems: 'center', gap: '12px', p: '13px 20px',
						bgcolor: L.bg, borderBottom: `1px solid ${L.border}`, flex: 'none',
					}}>
						<MsgAvatar name={clientName} size={42} fontSize={15} />
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Box component="strong" sx={{
								display: 'block', fontSize: '16.5px', fontWeight: 750, color: L.text,
								whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
							}}>
								{clientName}
							</Box>
							{selectedClient.phone_number && (
								<Box component="span" sx={{ display: 'block', fontSize: '13px', color: L.muted, fontWeight: 600, mt: '2px' }}>
									{selectedClient.phone_number}
								</Box>
							)}
						</Box>
						{aiButtonDesktop}
					</Box>

					{!selectedClient.phone_number && (
						<Alert severity="warning" sx={{ m: '8px 16px 0', fontFamily: L.font }}>
							This client does not have a phone number. Please add one to send messages.
						</Alert>
					)}

					{/* Messages */}
					<Box className="lav-overlay-scroll" sx={{
						flex: 1, minHeight: 0, overflowY: 'auto', p: '18px 24px',
						display: 'flex', flexDirection: 'column', gap: '10px',
					}}>
						<OverlayScrollThumb />
						{renderMessages()}
					</Box>

					{renderErrorAlert()}

					{/* Composer */}
					<Box sx={{
						display: 'flex', alignItems: 'flex-end', gap: '9px', p: '13px 20px 16px',
						bgcolor: L.bg, borderTop: `1px solid ${L.border}`, flex: 'none',
					}}>
						{composerInput('desktop')}
						{sendButton('desktop')}
					</Box>
				</>
			)}
		</Box>
	)

	const renderMobileThread = () => (
		<Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
			{/* Thread header */}
			<Box sx={{
				flex: 'none', display: 'flex', alignItems: 'center', gap: '8px', p: '10px 10px 9px',
				bgcolor: L.bg, borderBottom: `1px solid ${L.border}`,
			}}>
				<Box
					component="button"
					type="button"
					aria-label="Back"
					onClick={() => setSelectedClient(null)}
					sx={{
						flex: 'none', width: 34, height: 38, border: 'none', bgcolor: 'transparent',
						color: L.primary, display: 'flex', alignItems: 'center', justifyContent: 'center',
						borderRadius: '8px', cursor: 'pointer', '&:active': { bgcolor: L.surface },
					}}
				>
					<MsgIcon name="back" size={22} />
				</Box>
				<Box
					component="button"
					type="button"
					onClick={() => setMobileView('profile')}
					sx={{
						flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '9px',
						border: 'none', bgcolor: 'transparent', p: 0, textAlign: 'left',
						cursor: 'pointer', fontFamily: 'inherit',
					}}
				>
					<MsgAvatar name={clientName} size={34} fontSize={13} />
					<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
						<Box component="strong" sx={{
							fontSize: '15.5px', fontWeight: 750, color: L.text,
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							{clientName}
						</Box>
						{selectedClient.phone_number && (
							<Box component="span" sx={{
								fontSize: '11.5px', color: L.muted, fontWeight: 600,
								whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
							}}>
								{selectedClient.phone_number}
							</Box>
						)}
					</Box>
				</Box>
				{/* Persistent AI booking button */}
				<Box
					component="button"
					type="button"
					aria-label="Create appointment with AI"
					onClick={handleAiPrefill}
					disabled={aiBusy}
					sx={{
						flex: 'none', width: 38, height: 38, border: '1.5px solid #D7CCF5',
						borderRadius: '11px', bgcolor: L.field, color: L.primaryDeep,
						display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
						'&:active': { bgcolor: L.primarySoft },
						'&:disabled': { opacity: 0.6, cursor: 'default' },
					}}
				>
					{aiBusy
						? <CircularProgress size={16} sx={{ color: L.primaryDeep }} />
						: <MsgIcon name="auto" size={17} />}
				</Box>
				<Box
					component="button"
					type="button"
					aria-label="Details"
					onClick={() => setMobileView('profile')}
					sx={{
						flex: 'none', width: 36, height: 36, border: 'none', bgcolor: 'transparent',
						color: L.primary, display: 'flex', alignItems: 'center', justifyContent: 'center',
						cursor: 'pointer',
					}}
				>
					<MsgIcon name="info" size={21} />
				</Box>
			</Box>

			{!selectedClient.phone_number && (
				<Alert severity="warning" sx={{ m: '8px 12px 0', fontFamily: L.font }}>
					This client does not have a phone number. Please add one to send messages.
				</Alert>
			)}

			{/* Messages */}
			<Box className="lav-overlay-scroll" sx={{
				flex: 1, minHeight: 0, overflowY: 'auto', p: '14px 14px 6px',
				display: 'flex', flexDirection: 'column', gap: '9px', bgcolor: L.surface,
			}}>
				<OverlayScrollThumb />
				{renderMessages()}
			</Box>

			{renderErrorAlert()}

			{/* Composer */}
			<Box sx={{
				flex: 'none', display: 'flex', alignItems: 'flex-end', gap: '8px',
				p: '8px 10px 12px', bgcolor: L.bg, borderTop: `1px solid ${L.border}`,
			}}>
				{composerInput('mobile')}
				{sendButton('mobile')}
			</Box>
		</Box>
	)

	const renderMobileProfile = () => (
		<Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: L.bg }}>
			<Box sx={{
				position: 'relative', flex: 'none', display: 'flex', alignItems: 'center',
				justifyContent: 'center', p: '12px', bgcolor: L.bg, borderBottom: `1px solid ${L.border}`,
			}}>
				<Box
					component="button"
					type="button"
					onClick={() => setMobileView('thread')}
					sx={{
						position: 'absolute', left: 8, display: 'flex', alignItems: 'center', gap: '2px',
						border: 'none', bgcolor: 'transparent', color: L.primary, fontFamily: 'inherit',
						fontSize: '15px', fontWeight: 600, p: '4px 6px', cursor: 'pointer',
					}}
				>
					<MsgIcon name="back" size={20} />
					<span>Messages</span>
				</Box>
				<Box component="strong" sx={{ fontSize: '16px', fontWeight: 750, color: L.text }}>
					Details
				</Box>
			</Box>
			<Box className="lav-overlay-scroll" sx={{
				flex: 1, minHeight: 0, overflowY: 'auto', p: '18px 16px 36px',
				display: 'flex', flexDirection: 'column', gap: '20px',
			}}>
				<OverlayScrollThumb />
				{/* Identity head — tapping jumps to the client's profile on the
				    Clients tab (same affordance as the desktop sidebar). */}
				<Box
					component={onOpenClientProfile ? 'button' : 'div'}
					type={onOpenClientProfile ? 'button' : undefined}
					onClick={onOpenClientProfile ? () => onOpenClientProfile(selectedClient) : undefined}
					aria-label={onOpenClientProfile ? `Open ${clientName}’s profile` : undefined}
					sx={{
						display: 'flex', flexDirection: 'column', alignItems: 'center',
						...(onOpenClientProfile ? {
							border: 'none', bgcolor: 'transparent', color: 'inherit', fontFamily: 'inherit',
							borderRadius: L.r, p: '10px 8px', m: '-10px -8px -6px', cursor: 'pointer',
							'&:active': { bgcolor: L.surface },
						} : {}),
					}}
				>
					<MsgAvatar name={clientName} size={72} fontSize={26} />
					<Box component="strong" sx={{ fontSize: '19px', fontWeight: 800, lineHeight: 1.25, mt: '8px', color: L.text }}>
						{clientName}
					</Box>
					{selectedClient.phone_number && (
						<Box component="span" sx={{ fontSize: '14px', color: L.muted, fontWeight: 600, mt: '3px' }}>
							{selectedClient.phone_number}
						</Box>
					)}
					{onOpenClientProfile && <ViewProfileLink fontSize="13px" />}
				</Box>
				<ProfileSections
					pets={profilePets}
					upcoming={profileUpcoming}
					loading={profileLoading}
					tz={merchantTz}
					formatCurrency={formatCurrency}
				/>
			</Box>
		</Box>
	)

	return (
		<Box sx={{
			display: 'flex', height: '100%', overflow: 'hidden',
			fontFamily: L.font, color: L.text, bgcolor: L.bg,
		}}>
			{isMobile ? (
				!selectedClient
					? renderConversationList()
					: mobileView === 'profile'
						? renderMobileProfile()
						: renderMobileThread()
			) : (
				<>
					{renderConversationList()}
					{renderDesktopThread()}
					{showProfileSidebar && selectedClient && (
						<DesktopProfileSidebar
							clientName={clientName}
							phone={selectedClient.phone_number}
							pets={profilePets}
							upcoming={profileUpcoming}
							loading={profileLoading}
							tz={merchantTz}
							formatCurrency={formatCurrency}
							onViewProfile={onOpenClientProfile ? () => onOpenClientProfile(selectedClient) : null}
						/>
					)}
				</>
			)}

			{/* AI-prefilled Add Appointment Dialog.
			    - Mobile: AddAppointmentDialog renders its own bottom Drawer.
			    - Desktop: we wrap its 370px panel in a MUI Dialog so it floats as a
			      centered card instead of being squeezed by this page's flex layout. */}
			{isMobile ? (
				<AddAppointmentDialog
					open={addAppointmentOpen}
					onClose={closeAddAppointment}
					merchant={merchant}
					onAppointmentCreated={handleAppointmentCreated}
					prefilledAppointment={prefilledAppointmentData}
					aiContext={aiContext}
				/>
			) : (
				<Dialog
					open={addAppointmentOpen}
					onClose={closeAddAppointment}
					maxWidth={false}
					PaperProps={{
						sx: {
							// Design .msg-aiform: the AI appointment modal runs 400px wide.
							width: 400,
							maxWidth: '90vw',
							height: '90vh',
							maxHeight: '90vh',
							borderRadius: '12px',
							overflow: 'hidden',
						},
					}}
				>
					<AddAppointmentDialog
						open={addAppointmentOpen}
						onClose={closeAddAppointment}
						merchant={merchant}
						onAppointmentCreated={handleAppointmentCreated}
						prefilledAppointment={prefilledAppointmentData}
						aiContext={aiContext}
					/>
				</Dialog>
			)}

			{/* AI feedback — errors from parsing, or booking confirmation. */}
			<Snackbar
				open={!!aiSnack}
				autoHideDuration={6000}
				onClose={() => setAiSnack(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				{aiSnack ? (
					<Alert
						onClose={() => setAiSnack(null)}
						severity={aiSnack.severity}
						sx={{ width: '100%', fontFamily: L.font }}
					>
						{aiSnack.message}
					</Alert>
				) : undefined}
			</Snackbar>
		</Box>
	)
}

export default MessagesManager
